import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { TextField, CircularProgress, IconButton, InputAdornment, Snackbar, Alert, Chip } from '@mui/material';
import { AppButton } from '../../../shared/components/AppButton';
import { Visibility, VisibilityOff, ArrowBack, Folder, CheckCircle, Close } from '@mui/icons-material';
import { userService } from '../../../shared/services/userService';
import { projectService } from '../../../shared/services/projectService';

export const AddEditUser: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id?: string }>();
    const isAdd = !id;

    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [passwordConfirmation, setPasswordConfirmation] = useState('');
    const [idleTime, setIdleTime] = useState(2);
    const [screenshotTime, setScreenshotTime] = useState(5);
    const [projectIds, setProjectIds] = useState<string[]>([]);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [alert, setAlert] = useState({ show: false, type: 'success' as 'success' | 'error', message: '' });
    
    const [userDetails, setUserDetails] = useState<any>({});
    const [projects, setProjects] = useState<any[]>([]);

    useEffect(() => {
        getProjectList();
        if (id) {
            loadUser(id);
        }
    }, [id]);

    const getProjectList = async () => {
        try {
            const response = await projectService.projectList();
            if (response.success) {
                setProjects(response.data.items || []);
            }
        } catch (error) {
            console.error('Error loading projects:', error);
        }
    };

    const loadUser = async (userId: string) => {
        try {
            const response = await userService.getDetails(userId);
            const user = response.data.result || response.data || response;
            
            setUserDetails(user);
            setName(user.name || '');
            setEmail(user.email || '');
            setIdleTime(user.idle_time || 2);
            setScreenshotTime(user.screenshot_time || 5);
            setProjectIds(user.project_id || []);
        } catch (err: any) {
            setAlert({ 
                show: true, 
                type: 'error', 
                message: err.response?.data?.message || 'Error loading user details' 
            });
        }
    };

    const validate = (): boolean => {
        const newErrors: { [key: string]: string } = {};

        if (!name || name.trim() === '') {
            newErrors.name = 'Name is required.';
        }

        if (!email || email.trim() === '') {
            newErrors.email = 'Email is required.';
        } else if (!/\S+@\S+\.\S+/.test(email)) {
            newErrors.email = 'Please enter valid email.';
        }

        if (isAdd && (!password || password.trim() === '')) {
            newErrors.password = 'Password is required.';
        }

        if (isAdd && (!passwordConfirmation || passwordConfirmation.trim() === '')) {
            newErrors.passwordConfirmation = 'Confirm Password is required.';
        } else if (password && passwordConfirmation && password !== passwordConfirmation) {
            newErrors.passwordConfirmation = 'Password do not match.';
        }

        if (!idleTime || idleTime < 2) {
            newErrors.idleTime = 'Minimum required idle time is 2.';
        }

        if (!screenshotTime || screenshotTime < 1) {
            newErrors.screenshotTime = 'Minimum required screenshot time is 1.';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!validate()) {
            return;
        }

        setIsLoading(true);

        try {
            let finalProjectIds = projectIds;
            if (!finalProjectIds.length) {
                finalProjectIds = projects.map(p => p._id);
            }

            const body: any = {
                name,
                email,
                idle_time: idleTime,
                screenshot_time: screenshotTime,
                project_id: finalProjectIds
            };

            if (password && password.trim() !== '') {
                body.password = password;
            }

            if (isAdd) {
                await userService.add(body);
            } else {
                await userService.edit(userDetails._id || id, body);
            }

            setAlert({ show: true, type: 'success', message: isAdd ? 'User added successfully' : 'User updated successfully' });
            setTimeout(() => {
                navigate('/users');
            }, 1000);
        } catch (err: any) {
            console.error('Error saving user:', err);
            let errorMessage = 'Unknown error occurred';
            if (err.response?.data?.data?.errors) {
                errorMessage = Object.values(err.response.data.data.errors).join(', ');
            } else if (err.response?.data?.message) {
                errorMessage = err.response.data.message;
            }
            setAlert({ show: true, type: 'error', message: errorMessage });
        } finally {
            setIsLoading(false);
        }
    };

    const isSelected = (projectId: string): boolean => {
        return projectIds.includes(projectId);
    };

    const toggleSelection = (projectId: string) => {
        const current = [...projectIds];
        if (current.includes(projectId)) {
            setProjectIds(current.filter(id => id !== projectId));
        } else {
            setProjectIds([...current, projectId]);
        }
    };

    const removeProject = (projectId: string) => {
        setProjectIds(projectIds.filter(id => id !== projectId));
    };

    return (
        <div className="add-edit-user-container">
            <div className="add-edit-user-content">
                <div className="form-wrapper max-w-6xl">
                    <div className="-ml-4">
                        <Link to="/users" className="back-link">
                            <ArrowBack className="text-[#09c2c1]" />
                            <span className="ml-2">Back</span>
                        </Link>
                    </div>
                    <div className="title text-2xl sm:text-5xl">
                        {isAdd ? 'Add' : 'Edit'} User
                    </div>

                    {/* Form */}
                    <div className="form-card">
                        {alert.show && (
                            <div className={`alert mb-8 ${alert.type === 'error' ? 'alert-error' : 'alert-success'}`}>
                                {alert.message}
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="form" >
                            <div className="mb-6">
                                <div className="text-2xl font-bold tracking-tight ">Submit your request</div>
                            </div>

                            {/* Name */}
                            <TextField
                                label="Name"
                                placeholder="Name"
                                
                                value={name}
                                onChange={(e) => {
                                    setName(e.target.value);
                                    if (errors.name) setErrors({ ...errors, name: '' });
                                }}
                                error={!!errors.name}
                                helperText={errors.name}
                                fullWidth
                                required
                            />

                            {/* Email */}
                            <TextField
                                label="Email"
                                placeholder="Email"
                                type="email"
                                value={email}
                                onChange={(e) => {
                                    setEmail(e.target.value);
                                    if (errors.email) setErrors({ ...errors, email: '' });
                                }}
                                error={!!errors.email}
                                helperText={errors.email}
                                fullWidth
                                required
                                autoComplete="off"
                            />

                            {/* Password */}
                            <TextField
                                label="Password"
                                type={showPassword ? 'text' : 'password'}
                                placeholder="Password"
                                value={password}
                                onChange={(e) => {
                                    setPassword(e.target.value);
                                    if (errors.password) setErrors({ ...errors, password: '' });
                                }}
                                error={!!errors.password}
                                helperText={errors.password}
                                fullWidth
                                required={isAdd}
                                autoComplete="off"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setShowPassword(!showPassword)}>
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />

                            {/* Confirm Password */}
                            <TextField
                                label="Confirm Password"
                                type={showConfirmPassword ? 'text' : 'password'}
                                placeholder="Confirm Password"
                                value={passwordConfirmation}
                                onChange={(e) => {
                                    setPasswordConfirmation(e.target.value);
                                    if (errors.passwordConfirmation) setErrors({ ...errors, passwordConfirmation: '' });
                                }}
                                error={!!errors.passwordConfirmation}
                                helperText={errors.passwordConfirmation}
                                fullWidth
                                required={isAdd}
                                autoComplete="off"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                                                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />

                            {/* Idle Time and Screenshot Time */}
                            <div className="flex gap-4">
                                <TextField
                                    label="Idle Time (min)"
                                    type="number"
                                    value={idleTime}
                                    onChange={(e) => {
                                        setIdleTime(Number(e.target.value));
                                        if (errors.idleTime) setErrors({ ...errors, idleTime: '' });
                                    }}
                                    error={!!errors.idleTime}
                                    helperText={errors.idleTime}
                                    fullWidth
                                    required
                                    inputProps={{ min: 2 }}
                                />
                                <TextField
                                    label="Screenshot Time (min)"
                                    type="number"
                                    value={screenshotTime}
                                    onChange={(e) => {
                                        setScreenshotTime(Number(e.target.value));
                                        if (errors.screenshotTime) setErrors({ ...errors, screenshotTime: '' });
                                    }}
                                    error={!!errors.screenshotTime}
                                    helperText={errors.screenshotTime}
                                    fullWidth
                                    required
                                    inputProps={{ min: 1 }}
                                />
                            </div>

                            {/* Project Selection */}
                            <div className="project-selection-container">
                                <div className="project-header">
                                    <div className="project-title">
                                        <Folder className="project-icon text-[#09c2c1]" />
                                        Assign Projects
                                    </div>
                                    <div className="project-subtitle">Select the projects this user will have access to</div>
                                </div>
                                
                                {projects && projects.length > 0 ? (
                                    <>
                                        <div className="project-grid">
                                            {projects.map(project => (
                                                <div 
                                                    key={project._id}
                                                    className={`project-card ${isSelected(project._id) ? 'project-card--selected' : ''}`}
                                                    onClick={() => toggleSelection(project._id)}
                                                >
                                                    <div className="project-card__content">
                                                        <div className="project-card__info">
                                                            <div className="project-card__name">{project.project_name}</div>
                                                            {isSelected(project._id) && (
                                                                <div className="project-card__status">
                                                                    <CheckCircle className="status-icon" />
                                                                    <span>Selected</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="project-card__action">
                                                            {isSelected(project._id) && (
                                                                <Close 
                                                                    className="remove-icon" 
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        removeProject(project._id);
                                                                    }}
                                                                />
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        
                                        {projectIds.length > 0 && (
                                            <div className="project-summary">
                                                <div className="summary-badge">
                                                    <CheckCircle className="summary-icon text-white" />
                                                    <span className="summary-text">{projectIds.length} project{projectIds.length > 1 ? 's' : ''} selected</span>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className="project-empty">
                                        <div className="empty-text">No Projects Available</div>
                                        <div className="empty-subtext">There are no projects to assign at the moment</div>
                                    </div>
                                )}
                            </div>

                            {/* Action Buttons */}
                            <div className="form-actions">
                                <AppButton
                                    type="button"
                                    variant="cancel"
                                    onClick={() => navigate('/users')}
                                >
                                    Cancel
                                </AppButton>
                                <AppButton
                                    type="submit"
                                    variant="primary"
                                    disabled={isLoading}
                                    loading={isLoading}
                                >
                                    {isLoading ? 'Saving...' : (isAdd ? 'Save' : 'Update')}
                                </AppButton>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            {/* Snackbar */}
            <Snackbar open={alert.show} autoHideDuration={3000} onClose={() => setAlert({ ...alert, show: false })}>
                <Alert severity={alert.type}>{alert.message}</Alert>
            </Snackbar>
        </div>
    );
};
